import React, { useState, useRef } from "react";
import { Formik } from "formik";
import { connect } from "react-redux";
import { TextField } from "@material-ui/core";
import clsx from "clsx";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import * as auth from "../../store/ducks/auth.duck";
import { login, me } from "../../crud/auth.crud";
import MfaVerify from "./MfaVerify";

const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY;

function Login(props) {
  const [loading, setLoading] = useState(false);
  const [loadingButtonStyle, setLoadingButtonStyle] = useState({
    paddingRight: "2.5rem",
  });
  const [mfaData, setMfaData] = useState(null);
  const [pendingValues, setPendingValues] = useState(null);
  const captchaRef = useRef(null);

  const enableLoading = () => {
    setLoading(true);
    setLoadingButtonStyle({ paddingRight: "3.5rem" });
  };

  const disableLoading = () => {
    setLoading(false);
    setLoadingButtonStyle({ paddingRight: "2.5rem" });
  };

  const validateAndStoreUser = async (authToken) => {
    const meResponse = await me();
    const user = meResponse.data;

    if (user.identity?.ctx !== "admin") {
      throw new Error("Contexto inválido para este painel.");
    }

    if (user.session?.origin !== "web") {
      throw new Error("Origem inválida para este painel.");
    }

    props.login({ user, authToken });
  };

  const performLogin = async (captchaToken, setStatus, setSubmitting) => {
    const { email, password } = pendingValues;

    try {
      const res = await login(email, password, captchaToken);

      if (res.data.mfa_required) {
        disableLoading();
        setMfaData(res.data);
        return;
      }

      await validateAndStoreUser(res.data.token);
      disableLoading();
    } catch (err) {
      disableLoading();
      setSubmitting(false);

      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Usuário ou senha incorretos.";
      setStatus(errorMessage);
    } finally {
      setPendingValues(null);
      if (captchaRef.current) captchaRef.current.resetCaptcha();
    }
  };

  const handleCaptchaVerify = (token) => {
    if (pendingValues) {
      performLogin(token, pendingValues.setStatus, pendingValues.setSubmitting);
    }
  };

  const handleCaptchaError = () => {
    disableLoading();
    if (pendingValues) {
      pendingValues.setSubmitting(false);
      pendingValues.setStatus(
        "Erro na verificação de segurança. Tente novamente."
      );
    }
    setPendingValues(null);
  };

  if (mfaData) {
    return (
      <MfaVerify
        mfaData={mfaData}
        onBack={() => setMfaData(null)}
        onSuccess={validateAndStoreUser}
      />
    );
  }

  return (
    <>
      <div className="kt-login__head"></div>

      <div className="kt-login__body">
        <div className="kt-login__form">
          <div className="kt-login__title">
            <h3>Entrar</h3>
          </div>

          <Formik
            initialValues={{ email: "", password: "" }}
            validate={(values) => {
              const errors = {};

              if (!values.email) {
                errors.email = "O e-mail é uma informação obrigatória.";
              } else if (
                !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
              ) {
                errors.email = "Você precisa informar um e-mail válido.";
              }

              if (!values.password) {
                errors.password = "A senha é uma informação obrigatória.";
              }

              return errors;
            }}
            onSubmit={(values, { setStatus, setSubmitting }) => {
              enableLoading();
              setPendingValues({ ...values, setStatus, setSubmitting });

              if (HCAPTCHA_SITE_KEY && captchaRef.current) {
                captchaRef.current.execute();
              } else {
                performLogin(null, setStatus, setSubmitting);
              }
            }}
          >
            {({
              values,
              status,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form
                noValidate={true}
                autoComplete="off"
                className="kt-form"
                onSubmit={handleSubmit}
              >
                {status ? (
                  <div role="alert" className="alert alert-danger">
                    <div className="alert-text">{status}</div>
                  </div>
                ) : null}

                <div className="form-group">
                  <TextField
                    type="email"
                    label="Seu e-mail"
                    margin="normal"
                    className="kt-width-full"
                    name="email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.email}
                    helperText={touched.email && errors.email}
                    error={Boolean(touched.email && errors.email)}
                  />
                </div>

                <div className="form-group">
                  <TextField
                    type="password"
                    margin="normal"
                    label="Sua senha"
                    className="kt-width-full"
                    name="password"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.password}
                    helperText={touched.password && errors.password}
                    error={Boolean(touched.password && errors.password)}
                  />
                </div>

                <div>
                  <a className="forgot-password" href="/password/forgot">
                    Esqueceu sua senha?
                  </a>
                </div>

                <div className="kt-login__actions">
                  <button
                    id="kt_login_signin_submit"
                    type="submit"
                    disabled={loading}
                    className={`btn btn-primary btn-elevate kt-login__btn-primary 
                    						${clsx({
                                  "kt-spinner kt-spinner--right kt-spinner--md kt-spinner--light": loading,
                                })}`}
                    style={loadingButtonStyle}
                  >
                    Entrar!
                  </button>
                </div>

                {HCAPTCHA_SITE_KEY && (
                  <HCaptcha
                    ref={captchaRef}
                    sitekey={HCAPTCHA_SITE_KEY}
                    size="invisible"
                    onVerify={handleCaptchaVerify}
                    onExpire={handleCaptchaError}
                    onError={handleCaptchaError}
                  />
                )}
              </form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
}

export default connect(null, auth.actions)(Login);
